import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { exchangeFirebaseTokenForUserInfo } from '../shared/api/auth';
import { clearSmsOptInRemindLater } from '../shared/utils/smsOptIn';

/** Auto-logout after this many ms of no user activity (default 30 minutes). */
const INACTIVITY_MS = 30 * 60 * 1000;
/** Throttle activity resets so we don't reset the timer on every mousemove. */
const ACTIVITY_THROTTLE_MS = 1000;

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  /** When token exchange fails (e.g. backend error, no account), show this so the UI can explain why data is missing. */
  const [authError, setAuthError] = useState(null);

  // Firebase Auth keeps one user per browser profile (per origin). Two tabs/windows in the same
  // browser share that user; signing in elsewhere replaces it. For two users at once use two
  // browsers or one normal + one incognito window (see User Guide: Demo: two users).
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setUserInfo(null);
        setAuthError(null);
        sessionStorage.removeItem('userInfo');
        // Allow SMS opt-in prompt again on the next login (same tab).
        clearSmsOptInRemindLater();
        setLoading(false);
        return;
      }
      setUser(firebaseUser);
      setAuthError(null);
      try {
        const resolvedUserInfo = await exchangeFirebaseTokenForUserInfo(firebaseUser);
        setUserInfo(resolvedUserInfo);
        if (resolvedUserInfo != null) {
          sessionStorage.setItem('userInfo', JSON.stringify(resolvedUserInfo));
        } else {
          sessionStorage.removeItem('userInfo');
        }
      } catch (err) {
        const message = err && typeof err.message === 'string' ? err.message : 'Could not load account information. Please log in again.';
        setAuthError(message);
        setUserInfo(null);
        sessionStorage.removeItem('userInfo');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Auto-logout after inactivity (only when user is signed in).
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(0);
  useEffect(() => {
    if (!user) return;

    const resetInactivityTimer = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityRef.current = now;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        signOut(auth);
      }, INACTIVITY_MS);
    };

    resetInactivityTimer(); // start timer on mount

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, resetInactivityTimer));

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, resetInactivityTimer));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, [user]);

  const setUserInfoAndStorage = (info) => {
    setUserInfo(info);
    setAuthError(null);
    if (info != null) {
      sessionStorage.setItem('userInfo', JSON.stringify(info));
    } else {
      sessionStorage.removeItem('userInfo');
    }
  };

  const hasPermission = (permissionId) => {
    if (!userInfo) return false;
    if (userInfo.permissions === '*') return true;
    const roles = userInfo.roles || [];
    if (roles.includes('Super Admin')) return true;
    const p = userInfo.permissions;
    if (p === '*') return true;
    return Array.isArray(p) && p.includes(permissionId);
  };

  const hasRole = (roleName) => {
    if (!userInfo) return false;
    const roles = userInfo.roles || [];
    return roles.includes(roleName);
  };

  const value = {
    user,
    userInfo,
    loading,
    authError,
    setUserInfo: setUserInfoAndStorage,
    hasPermission,
    hasRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
